import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type { VoiceAgentCard } from '../pages/agents/VoiceAgents';

/**
 * The Carrier Correspondence agent's screens: cases stuck with a carrier, and
 * the calls that ask why.
 *
 * Read-only but for one thing — asking for a call, which rings a real carrier
 * and so is the one endpoint that is guarded.
 *
 * Names and numbers arrive whole. Times are UTC ISO strings ("2026-09-26T09:30:00Z");
 * show them in the viewer's own time.
 */

/** Who has to move next, once a call has said. */
export type Side = 'carrier' | 'agency' | 'client';

export type CaseStatus = 'waiting' | 'calling' | 'call_ended' | 'answered' | 'needs_person';

export interface CcCase {
  id: string;
  policy_num: string;
  carrier: string;
  product: string | null;
  applicant: string;
  state: string | null;
  face_amount: number | null;
  premium: number | null;
  /** Where the carrier's own system says it is stuck. */
  stage: string;
  stage_since: string | null;
  /** Worked out on the server on every read — never a stored count. */
  days_stalled: number;
  status: CaseStatus;
  attempts: number;
  last_call_at: string | null;
  /** Everything below is empty until a call has been made. */
  reason: string | null;
  blocking_item: string | null;
  blocking_side: Side | null;
  expected_by: string | null;
  reference_number: string | null;
  rep_name: string | null;
}

export interface CcTurn {
  role: 'agent' | 'user';
  message: string;
  seconds: number;
}

export interface CcCall {
  id: string;
  case_id: string | null;
  agent: string | null;
  started_at: string | null;
  duration_secs: number | null;
  status: string;
  live: boolean;
  outcome: string | null;
  summary: string | null;
  transcript: CcTurn[];
  has_transcript: boolean;
  policy_num?: string | null;
  carrier?: string | null;
  applicant?: string | null;
}

export interface CcCallsByDay {
  day: string;
  calls: number;
  answered: number;
  no_answer: number;
  needs_person: number;
}

export interface CcStep {
  at: string | null;
  kind: string;
  label: string;
  detail: string | null;
  actor: string | null;
  actor_kind: 'ai' | 'human' | null;
}

export interface CcStory {
  case: CcCase;
  steps: CcStep[];
  calls: CcCall[];
}

export interface CcCases {
  cases: CcCase[];
  /** Carrier, not owner: a case belongs to the agency's case manager, not us. */
  carriers: string[];
}

export interface CcSpotlight {
  totals: {
    stalled: number;
    median_days_stalled: number | null;
    longest_days_stalled: number | null;
    investigated: number;
    calls_made: number;
    waiting_on_us: number;
    /** Time the AI spent on hold and on the phone with carriers. */
    hold_seconds: number;
  };
  /** Ordered as the chart should stack them, busiest carrier first. */
  carriers: string[];
  /** Each stage's total, plus how that total splits across the carriers. */
  by_stage: { stage: string; cases: number; by_carrier: Record<string, number> }[];
  by_carrier: { carrier: string; cases: number }[];
  whose_move: { side: Side; label: string; cases: number }[];
  oldest: CcCase[];
}

export interface CcConfiguration {
  settings: {
    stalled_after_days: number;
    stalled_after_choices: number[];
    chase_times: number;
    chase_every_days: number;
    note: string;
  };
  /** The same shape ROCCO and Policy Servicing send, for the shared Voice agents section. */
  agents: VoiceAgentCard[];
  agent_facts: { label: string; value: string }[];
  provider_error: string | null;
  rules: { when: string; then: string; detail: string }[];
  finds_out: { key: string; label: string; detail: string }[];
  guardrails: { title: string; detail: string }[];
}

export interface CcIntegration {
  id: string;
  name: string;
  role: string;
  connected: boolean;
  built_on: string[];
  detail: string;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  return `${tenantApiPrefix(tid ?? '')}/carrier-correspondence`;
}

async function call<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), { ...init, headers: getAuthHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.json();
}

export const fetchCcCases = (scope: 'all' | 'open' | 'answered' = 'all') =>
  call<CcCases>(`/cases?scope=${scope}`, { method: 'GET' }, 'Could not load the cases');
export const fetchCcCase = (id: string) =>
  call<CcStory>(`/cases/${id}`, { method: 'GET' }, 'Could not load that case');
export const fetchCcCalls = () =>
  call<{ calls: CcCall[]; by_day: CcCallsByDay[] }>('/calls', { method: 'GET' }, 'Could not load the calls');
export const fetchCcSpotlight = () =>
  call<CcSpotlight>('/spotlight', { method: 'GET' }, 'Could not load Spotlight');
export const fetchCcConfiguration = () =>
  call<CcConfiguration>('/configuration', { method: 'GET' }, 'Could not load how the agent is set up');
export const fetchCcIntegrations = () =>
  call<{ integrations: CcIntegration[] }>('/integrations', { method: 'GET' }, 'Could not load integrations');

/**
 * Ring this case's carrier. The error message is the useful part — no number on
 * file, a call already running — so it is passed through rather than replaced.
 */
export const callTheCarrier = (id: string) =>
  call<CcStory>(`/cases/${id}/call`, { method: 'POST' }, 'Could not start the call');

/** Listen for "the cases changed" until `signal` aborts. Carries no data. */
export async function watchCases(onChange: () => void, signal: AbortSignal, onOpen?: () => void): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}/cases/stream`), {
    headers: { ...getAuthHeaders(), Accept: 'text/event-stream' }, signal,
  });
  if (!response.ok || !response.body) throw new Error(`Live updates unavailable (${response.status})`);
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) if (frame.includes('event: changed')) onChange();
  }
}
